'use client';

/**
 * Dashboard Notifications Widget
 * Shows personalized notifications for the user including:
 * - New content recommendations
 * - Streak warnings
 * - Badge progress
 * - Command Center alerts (if applicable)
 */

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { Bell, Flame, Trophy, BookOpen, Zap, X, ChevronRight, AlertTriangle } from 'lucide-react';

interface Notification {
  id: string;
  type: 'streak' | 'badge' | 'content' | 'milestone' | 'command-center';
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'success';
  link?: string;
  createdAt: string;
}

interface DashboardNotificationsProps {
  streakData?: {
    currentStreak: number;
    streakActive: boolean;
    lastActivityDate: string | null;
  };
  badgeData?: {
    earnedCount: number;
    totalCount: number;
    recentlyEarned: { name: string; id: string } | null;
  };
  commandCenterNotifications?: number;
}

export default function DashboardNotifications({
  streakData,
  badgeData,
  commandCenterNotifications = 0,
}: DashboardNotificationsProps) {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dashboard-dismissed-notifications');
      if (saved) {
        try {
          return new Set(JSON.parse(saved));
        } catch {
          return new Set();
        }
      }
    }
    return new Set();
  });

  // Generate notifications based on user data
  const notifications = useMemo(() => {
    const items: Notification[] = [];
    const now = new Date();

    // Streak notifications
    if (streakData) {
      // Streak at risk
      if (streakData.currentStreak > 0 && !streakData.streakActive) {
        items.push({
          id: 'streak-risk',
          type: 'streak',
          title: 'Streak at Risk!',
          message: `Your ${streakData.currentStreak}-day streak will end soon. Read an article to keep it going!`,
          severity: 'warning',
          link: '/insights',
          createdAt: now.toISOString(),
        });
      }

      // Streak milestones
      if (streakData.currentStreak === 7) {
        items.push({
          id: 'streak-7',
          type: 'milestone',
          title: 'One Week Streak!',
          message: 'You\'ve been learning for 7 days straight. Keep up the momentum!',
          severity: 'success',
          createdAt: now.toISOString(),
        });
      } else if (streakData.currentStreak === 30) {
        items.push({
          id: 'streak-30',
          type: 'milestone',
          title: 'Monthly Streak!',
          message: 'Incredible! 30 days of consistent learning. You\'re committed!',
          severity: 'success',
          createdAt: now.toISOString(),
        });
      }
    }

    // Badge notifications
    if (badgeData) {
      // Recently earned badge
      if (badgeData.recentlyEarned) {
        items.push({
          id: `badge-earned-${badgeData.recentlyEarned.id}`,
          type: 'badge',
          title: 'New Badge Earned!',
          message: `You earned the "${badgeData.recentlyEarned.name}" badge.`,
          severity: 'success',
          link: '/dashboard/achievements',
          createdAt: now.toISOString(),
        });
      }

      // Progress towards next badge
      const progress = badgeData.totalCount > 0
        ? Math.round((badgeData.earnedCount / badgeData.totalCount) * 100)
        : 0;
      if (progress >= 75 && progress < 100) {
        items.push({
          id: 'badge-progress-75',
          type: 'badge',
          title: 'Almost There!',
          message: `You've earned ${progress}% of all badges. Keep going to collect them all!`,
          severity: 'info',
          link: '/dashboard/achievements',
          createdAt: now.toISOString(),
        });
      }
    }

    // Command Center notifications
    if (commandCenterNotifications > 0) {
      items.push({
        id: 'command-center',
        type: 'command-center',
        title: 'Command Center Alerts',
        message: `You have ${commandCenterNotifications} notification${commandCenterNotifications !== 1 ? 's' : ''} in Command Center.`,
        severity: 'warning',
        link: '/command-center',
        createdAt: now.toISOString(),
      });
    }

    // Filter out dismissed
    return items.filter(n => !dismissedIds.has(n.id));
  }, [streakData, badgeData, commandCenterNotifications, dismissedIds]);

  const dismissNotification = (id: string) => {
    setDismissedIds(prev => {
      const next = new Set(prev);
      next.add(id);
      if (typeof window !== 'undefined') {
        localStorage.setItem('dashboard-dismissed-notifications', JSON.stringify([...next]));
      }
      return next;
    });
  };

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'streak':
        return <Flame className="w-4 h-4" />;
      case 'badge':
        return <Trophy className="w-4 h-4" />;
      case 'content':
        return <BookOpen className="w-4 h-4" />;
      case 'milestone':
        return <Zap className="w-4 h-4" />;
      case 'command-center':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  const getSeverityStyles = (severity: Notification['severity']) => {
    switch (severity) {
      case 'warning':
        return 'bg-yellow-900/30 border-yellow-500/30 text-yellow-400';
      case 'success':
        return 'bg-green-900/30 border-green-500/30 text-green-400';
      case 'info':
      default:
        return 'bg-blue-900/30 border-blue-500/30 text-blue-400';
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="bg-surface rounded-lg border border-border-muted">
      <div className="px-4 py-3 border-b border-border-muted flex items-center gap-2">
        <Bell className="w-5 h-5 text-accent" />
        <h3 className="text-lg font-semibold text-foreground">Notifications</h3>
        {notifications.length > 0 && (
          <span className="bg-accent text-accent-foreground text-xs font-bold px-2 py-0.5 rounded-full">
            {notifications.length}
          </span>
        )}
      </div>

      <div className="divide-y divide-border-muted">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className="p-3 hover:bg-cream/5 transition-colors"
          >
            <div className="flex gap-3">
              <div className={`p-2 rounded-lg ${getSeverityStyles(notification.severity)}`}>
                {getIcon(notification.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <h4 className="text-foreground font-medium text-sm">
                      {notification.title}
                    </h4>
                    <p className="text-foreground-tertiary text-xs mt-0.5">
                      {notification.message}
                    </p>
                  </div>
                  <button
                    onClick={() => dismissNotification(notification.id)}
                    className="text-foreground-muted hover:text-foreground transition-colors p-1"
                    title="Dismiss"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
                {notification.link && (
                  <Link
                    href={notification.link}
                    className="inline-flex items-center gap-1 mt-2 text-xs text-accent hover:text-accent transition-colors"
                  >
                    View <ChevronRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
